import { businessModeFor, hasFeature } from '@/lib/business-profile';
import { can, canUseOffice } from '@/lib/capabilities';
import type { AuthStore } from '@/stores/AuthStore';
import type { OrganizationRole, OrganizationType } from '@/types/auth';
import { onlineOnlyPath } from '@/lib/sales-channels';

/**
 * Login destination and route scope policy (V2-1).
 *
 * ONE place decides two questions that used to be answered ad hoc in three:
 *
 *   1. where a signed-in person belongs the moment they arrive, and
 *   2. how much workstation a given route cannot run without.
 *
 * Both are pure functions of the verified organization role/type plus what has
 * been selected, so the router, the login screen and the setup screen cannot
 * disagree about it.
 *
 * ⚠ UX ONLY, exactly like src/lib/capabilities.ts. pos-api's
 * PosRoleMiddleware/PosTerminalMiddleware remain the boundary; sending someone
 * to the right screen is a convenience, never a permission.
 */

/**
 * Scope ladder. organization ⊂ branch ⊂ terminal — each level implies the ones
 * before it, which is why a single value describes a route's requirement.
 */
export type ScopeLevel = 'organization' | 'branch' | 'terminal';

export const OFFICE_HOME = '/';
export const REGISTER = '/pos';
export const FLOOR_MAP = '/pos/tables';
export const OFFICE_FLOOR_PLAN = '/tables';
export const QUEUE = '/pos/queue';
export const MENU = '/pos/menu';
export const TODAY = '/pos/today';
export const DEVICE = '/pos/device';
/**
 * Branch-scoped delivery charges. Restaurant-only in the office nav
 * (src/app/layouts/OfficeShell.tsx) because they are what the restaurant
 * checkout and the register's area picker read; an ecommerce organization
 * administers shipping zones instead. Named here so the ROUTE can be gated on
 * the same fact the link is, rather than merely being unlinked.
 */
export const OFFICE_DELIVERY_AREAS = '/delivery-areas';
/*
  Guarded by RequireRestaurant (router.tsx) and, until now, missing from
  `canUseRoute` below — so a retail manager's `?next=/branch-settings` was
  judged allowed, honoured, and then bounced by the guard on arrival. That
  round trip is the precise failure this function's own docblock says it
  exists to prevent.
*/
export const OFFICE_BRANCH_SETTINGS = '/branch-settings';
/**
 * The parcel model — the exact inverse of the line above.
 *
 * Priced shipping zones and the warehouses behind them are what an ecommerce
 * organization dispatches with; a restaurant register cannot read either, since
 * `deliveryPricingFor` answers 'area' for it (src/types/checkout.ts). Named here
 * for the same reason `OFFICE_DELIVERY_AREAS` is: so the routing policy can be
 * asked the question the guard tree answers, instead of the two drifting.
 */
export const OFFICE_SHIPPING = '/shipping';
export const OFFICE_WAREHOUSES = '/warehouses';
export const SETUP = '/setup';

/**
 * The one fact that decides whether tables exist at all.
 *
 * Strict equality against the VERIFIED organizations.type, never an inference
 * from anything else. A waiter role does not make an organization a
 * restaurant, and a null type is not a restaurant either — it is the backend
 * declining to say, which 07-roles-permissions.md §4 requires be treated as
 * "assume nothing", not as "assume the mode with the extra screens".
 *
 * That equality now lives ONCE, in `businessModeFor`
 * (src/lib/business-profile.ts:121-125), which is where the console states what
 * each organization type IS for the nav labels, the trade words and the feature
 * table. Expressing this over it makes the routing policy and that table agree
 * by construction rather than by two people remembering to — the failure the
 * module was written for was six independent spellings of the same split, one of
 * which always got missed. It changes nothing here: `businessModeFor` answers
 * 'restaurant' for the literal `'restaurant'` alone, and 'neutral' for null, for
 * an unknown value and for the unsupported `quotation`, so this stays true for
 * exactly one input and false for every other.
 *
 * The name and signature stay put because the ROUTING policy is written in terms
 * of this question (landingRouteFor:129, canUseRoute:172) and reaches the guard
 * tree through src/features/auth/index.ts. Note that
 * src/features/pos/queue/stages.ts:`resolveBusinessMode` answers null the OTHER
 * way; that is deliberate and is not this question — it picks column headings,
 * not whether a feature may be shown.
 */
export function isRestaurantType(organizationType: OrganizationType | null): boolean {
  return businessModeFor(organizationType) === 'restaurant';
}

/** What has been selected, plus the two verified facts that shape the answer. */
export interface WorkstationScope {
  /** Membership role for the SELECTED organization; null = backend did not say. */
  role: OrganizationRole | null;
  /** organizations.type; null = backend did not say — never guessed. */
  organizationType: OrganizationType | null;
  posEnabled?: boolean;
  onlineStoreEnabled?: boolean;
  hasOrganization: boolean;
  hasBranch: boolean;
  hasTerminal: boolean;
}

/** The store's answer to "what have we got", in the shape the policy reads. */
export function scopeOf(auth: AuthStore): WorkstationScope {
  return {
    role: auth.role,
    posEnabled: auth.organization?.salesControls?.pos_enabled,
    onlineStoreEnabled: auth.organization?.salesControls?.online_store_enabled,
    organizationType: auth.organization?.type ?? null,
    hasOrganization: auth.hasOrganization,
    hasBranch: auth.hasBranch,
    hasTerminal: auth.hasTerminal,
  };
}

/**
 * Where a signed-in person lands.
 *
 * - owner/manager → the office. Running a business is not a till operation, so
 *   they are never asked to nominate a terminal to read their own orders.
 * - cashier → the register. That is the job, and it is the one destination that
 *   genuinely needs a terminal.
 * - waiter, RESTAURANT → the floor. `tables.serve` is the only thing the server
 *   grants a waiter beyond reading the catalog: the register, the queue
 *   (`GET /orders`) and the day session are all `sell`/`shift.open`, i.e.
 *   cashier and up (pos-api/routes/api.php:126, 178-186).
 * - waiter, anything else → the menu. An ecommerce, quotation or
 *   unknown-type organization has no floor to send them to, and the role must
 *   never be read backwards as evidence that it does. `GET /products` carries
 *   no role gate (routes/api.php:95), so the catalog is the one surface that
 *   is certain to answer them — read-only, and already in their nav.
 * - no role reported (pre-roles backend) → assume nothing, exactly as
 *   07-roles-permissions.md §4 requires: a device that already has a terminal
 *   is a till and opens the register; anything else opens the office, which
 *   needs no workstation and hides nothing.
 */
export function landingRouteFor(scope: WorkstationScope): string {
  switch (scope.role) {
    case 'owner':
    case 'manager':
      return OFFICE_HOME;
    case 'cashier':
      return scope.posEnabled === false ? '/pos/today' : REGISTER;
    case 'waiter':
      return isRestaurantType(scope.organizationType) ? FLOOR_MAP : MENU;
    default:
      return scope.hasTerminal ? REGISTER : OFFICE_HOME;
  }
}

/** Trailing slashes are not a different screen. */
function normalizePath(pathname: string): string {
  const path = pathname.split('?')[0]?.split('#')[0] ?? '/';
  return path.replace(/\/+$/, '') || '/';
}

/**
 * May this person USE this screen at all — the same answer the guard tree
 * gives, in one place (V2-2 §4.1/§4.2).
 *
 * The router already refuses these paths with RequireSell/RequireRestaurant and
 * OfficeShellRoute's `office` check. This function is the SAME policy stated as
 * a value, so the code that decides where to SEND someone cannot contradict the
 * code that decides whether to let them stay: sending a cashier back to
 * /orders after setup, only for the office shell to bounce them again, is the
 * bug this closes. Keep it in step with:
 *
 *   - RequireSell           (src/app/router.tsx, /pos index + queue + today)
 *   - RequireRestaurant     (/pos/tables, /tables, /delivery-areas)
 *   - RequireShippingZones  (/shipping and the zone editor)
 *   - RequireWarehouses     (/warehouses)
 *   - OfficeShellRoute      (canUseOffice, every office route)
 *
 * ⚠ UX ONLY, like everything else here — pos-api authorizes every request.
 *
 * `hasOrganization === false` returns TRUE for anything: with no organization
 * selected there is no verified role and no verified type, so there is nothing
 * to judge with. Assuming a refusal there would throw away a deep link before
 * /setup has even asked which tenant this is (07-roles-permissions.md §4:
 * assume nothing).
 */
export function canUseRoute(scope: WorkstationScope, pathname: string): boolean {
  if (scope.hasOrganization && scope.onlineStoreEnabled === false && onlineOnlyPath(pathname)) return false;
  if (scope.hasOrganization && scope.posEnabled === false && normalizePath(pathname) === REGISTER) return false;
  if (!scope.hasOrganization) return true;
  const path = normalizePath(pathname);
  // Money and the shift that owns it: `sell`/`shift.open` on the server, so a
  // waiter meets a 403 at every one (pos-api/routes/api.php:126, 178-186).
  if (path === REGISTER || path === QUEUE || path === TODAY) return can(scope.role, 'sell');
  // Tables exist only in a restaurant, and only the VERIFIED type says so.
  if (
    path === FLOOR_MAP ||
    path === OFFICE_FLOOR_PLAN ||
    path === OFFICE_DELIVERY_AREAS ||
    path === OFFICE_BRANCH_SETTINGS
  ) {
    return isRestaurantType(scope.organizationType);
  }
  /*
    And the inverse: the parcel screens, which a restaurant must not be sent to.
    Without this, a restaurant owner signing in with ?next=/shipping is honoured,
    lands, and is immediately bounced by RequireShippingZones — one redirect the
    policy already knew how to avoid.

    Both halves of the guard tree, in the order the tree applies them:
    OfficeShellRoute's `office` check wraps every office route
    (src/app/pos-routes.tsx:503), and the feature guard sits inside it. Asked of
    `hasFeature` rather than `!isRestaurantType` so this cannot disagree with the
    guard, which asks the same table (src/lib/business-profile.ts:160-162).
    Zone editor paths (`/shipping/new`, `/shipping/:id/edit`) ride the same
    prefix.
  */
  if (path === OFFICE_SHIPPING || path.startsWith(`${OFFICE_SHIPPING}/`)) {
    return canUseOffice(scope.role) && hasFeature(scope.organizationType, 'shipping-zones');
  }
  if (path === OFFICE_WAREHOUSES) {
    return canUseOffice(scope.role) && hasFeature(scope.organizationType, 'warehouses');
  }
  // Menu and Device are open to every role: GET /products carries no role gate
  // (routes/api.php:95), and "Switch terminal" must stay reachable from a
  // device whose terminal is exactly what broke.
  if (path === MENU || path === DEVICE) return true;
  if (path.startsWith('/pos')) return true;
  // Everything else is the office, which is `office` (manager+) end to end.
  return canUseOffice(scope.role);
}

/**
 * Where to actually send someone who asked for `next`.
 *
 * `next` is honoured when the role/type can use it and replaced by their own
 * landing when it cannot. This is what makes "ask only for what the destination
 * needs" true for the REAL destination: a cashier deep-linked to /orders is
 * asked for a branch and a till once and arrives at the register, instead of
 * being asked for an organization, bounced out of the office and sent back
 * through setup a second time.
 */
export function destinationFor(scope: WorkstationScope, next: string | null): string {
  if (next && canUseRoute(scope, next)) return next;
  return landingRouteFor(scope);
}

/**
 * The scope a path cannot run without.
 *
 * Selling, the shift that owns the money, and physical stock work are
 * terminal-bound. Stock operations need the terminal's warehouse assignment;
 * reading the floor, queue or menu remains branch work. The office is
 * organization work and is deliberately NOT gated on a workstation at all.
 *
 * Verified against the server rather than assumed: PosTerminalMiddleware
 * VALIDATES an X-Terminal-Id when one is sent and passes the request straight
 * through when none is (pos-api/app/Http/Middleware/PosTerminalMiddleware.php:21-25).
 * So `GET /tables` behind `pos.terminal` (routes/api.php:140) genuinely serves a
 * waiter who has no till — the middleware gates the header, not the route.
 *
 * /pos/device is organization-only on purpose: it is where "Switch terminal"
 * lives, and requiring a valid terminal to reach the screen that replaces a
 * broken one would strand the device.
 *
 * V2-2: the path is normalized through the SAME helper canUseRoute uses, which
 * also strips a query string. Guards build `next` from
 * `location.pathname + location.search`, so `/pos?x=1` used to miss the
 * `=== REGISTER` test, be answered "branch", and send the user back to a
 * register that immediately returned them to setup — a redirect loop out of one
 * stray query parameter.
 */
export function requiredScopeFor(pathname: string): ScopeLevel {
  const path = normalizePath(pathname);
  if (path === REGISTER) return 'terminal';
  if (path === TODAY) return 'terminal';
  if (path === '/pos/stock') return 'terminal';
  if (path === DEVICE) return 'organization';
  if (path.startsWith('/pos')) return 'branch';
  return 'organization';
}

const SATISFIES: Record<ScopeLevel, (scope: WorkstationScope) => boolean> = {
  organization: (s) => s.hasOrganization,
  branch: (s) => s.hasBranch,
  terminal: (s) => s.hasTerminal,
};

/** True when `scope` already covers `level`. */
export function satisfiesScope(scope: WorkstationScope, level: ScopeLevel): boolean {
  return SATISFIES[level](scope);
}

/** `/setup` carrying where to return to once the workstation is good enough. */
export function setupPathFor(next: string): string {
  return `${SETUP}?next=${encodeURIComponent(next)}`;
}

/**
 * Marks a /setup visit as CHOSEN rather than bounced.
 *
 * `?next=` alone cannot carry that distinction, because it answers a different
 * question — "where do I go back to" — and "Switch terminal" has an answer to
 * it (the register) while being the most deliberate visit to setup there is.
 */
export const SETUP_CHOSEN_PARAM = 'switch';

/**
 * `/setup` for someone who came to RECONFIGURE this workstation, carrying where
 * to return to afterwards.
 *
 * Why this exists rather than reusing `setupPathFor`: setup treats the presence
 * of `?next=` as proof a guard turned the user away, and auto-returns the moment
 * the scope is good enough again. "Switch terminal" clears the terminal and then
 * arrives with a `next`, so on a branch with a single terminal the screen
 * re-selected that same terminal, decided it was satisfied, and forwarded
 * straight back to the register — the setup screen appeared for about a second
 * and vanished, which is the V2-3 "setup goes straight through" failure
 * returning through the one door still marked as a bounce.
 */
export function switchWorkstationPathFor(next: string): string {
  return `${setupPathFor(next)}&${SETUP_CHOSEN_PARAM}=1`;
}

/**
 * Read a `?next=` back off the setup URL.
 *
 * Same-origin ABSOLUTE PATHS only. A `next` that is a full URL, a protocol
 * relative `//host` or anything that is not a path is discarded rather than
 * navigated to — the value arrives from the address bar, so it is untrusted
 * input and an open redirect is not a workstation feature.
 */
export function safeNextPath(raw: string | null): string | null {
  if (!raw) return null;
  if (!raw.startsWith('/') || raw.startsWith('//')) return null;
  if (raw.startsWith('/setup') || raw.startsWith('/login')) return null;
  return raw;
}
